<template>
	<div class="GridAuto" :class="classes">
		<!-- @slot List of elements to position with grid -->
		<slot />
	</div>
</template>
<script>
export default {
	name: 'CloGridAuto',
	props: {
		/**
		 * Grid cell size. Medium (md) is default. Can be xs, sm, md, lg, xl.
		 */
		size: {
			type: String,
			default: '',
		},
		/**
		 * Spacing between cells. None is default. Can be xxs, xs, sm, md, lg, xl.
		 */
		gap: {
			type: String,
			default: '',
		},
		/**
		 * Cell vertical alignment. None is default. Can be top, middle, bottom, baseline.
		 */
		align: {
			type: String,
			default: '',
		},
		/**
		 * Try to fit all cells in one row
		 */
		noWrap: {
			type: Boolean,
			default: false,
		},
		/**
		 * A lone cell should not stretch to fill width
		 */
		noStretch: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes: function () {
			let classes = [];
			const prefix = 'GridAuto--';
			if (this.size.length) {
				classes.push(prefix + this.size);
			}
			if (this.gap.length) {
				classes.push(prefix + 'gap-' + this.gap);
			}
			if (this.align.length) {
				if (this.align === 'top') {
					classes.push(prefix + 'alignTop');
				} else {
					classes.push(prefix + this.align);
				}
			}
			if (this.noWrap) {
				classes.push(prefix + 'noWrap');
			}
			if (this.noStretch) {
				classes.push(prefix + 'noStretch');
			}
			return classes.join(' ');
		},
	},
	data() {
		return {};
	},
};
</script>
<style lang="scss">
// GridAuto styles

// Base class
.GridAuto,
.GridAuto--md {
	--grid-auto-size: 16rem;
	--grid-auto-gap: 0;
	display: flex;
	flex-wrap: wrap;
	width: 100%;
	max-width: 100%;
	& > * {
		flex-grow: 1;
		flex-basis: 16rem;
		@supports (display: grid) {
			margin: 0 !important;
		}
	}
	@supports (display: grid) {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(var(--grid-auto-size), 1fr));
		grid-gap: var(--grid-auto-gap);
	}
}

// Unordered list
ul.GridAuto {
	list-style: none;
	margin-left: 0;
	[dir='rtl'] & {
		margin-right: 0;
	}
}

// Size variants
.GridAuto--xs {
	--grid-auto-size: 8rem;
	& > * {
		flex-basis: 8rem;
	}
}

.GridAuto--sm {
	--grid-auto-size: 12rem;
	& > * {
		flex-basis: 12rem;
	}
}

.GridAuto--lg {
	--grid-auto-size: 20rem;
	& > * {
		flex-basis: 20rem;
	}
}

.GridAuto--xl {
	--grid-auto-size: 24rem;
	& > * {
		flex-basis: 24rem;
	}
}

.GridAuto--noWrap {
	flex-wrap: nowrap;
	grid-template-columns: repeat(auto-fill, 1fr);
}

// Gap variants
.GridAuto--gap,
.GridAuto--gap-md {
	--grid-auto-gap: 1rem;
	& > * {
		margin: 0.5rem;
	}
}

.GridAuto--gap-xxs {
	--grid-auto-gap: 1px;
	& > * {
		margin: 1px;
	}
}

.GridAuto--gap-xs {
	--grid-auto-gap: 0.25rem;
	& > * {
		margin: 0.125rem;
	}
}

.GridAuto--gap-sm {
	--grid-auto-gap: 0.5rem;
	& > * {
		margin: 0.25rem;
	}
}

.GridAuto--gap-lg {
	--grid-auto-gap: 2rem;
	& > * {
		margin: 1rem;
	}
}

.GridAuto--gap-xl {
	--grid-auto-gap: 4rem;
	& > * {
		margin: 2rem;
	}
}

// Alignment variants
.GridAuto--alignTop {
	align-items: start;
}
.GridAuto--middle {
	align-items: center;
}
.GridAuto--bottom {
	align-items: end;
}
.GridAuto--baseline {
	align-items: baseline;
}

// No stretch variant
.GridAuto--noStretch {
	grid-template-columns: repeat(auto-fill, minmax(var(--grid-auto-size), 1fr));
	& > * {
		flex-grow: 0;
	}
}
</style>
